import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Chip from '@material-ui/core/Chip';
import Button from '../components/common/Button';
import ButtonGroup from '../components/common/ButtonGroup';
import DataTypes from '../components/Table/dataTypes';
import Table from '../components/Table/Table';
import CellValueHighlighter from '../components/Table/Cell/CellValueHighlighter';
import useDishes from '../features/DishContainer/hooks/useDishes';
import DishColumns from '../features/DishContainer/columns';
import useExcludeDishes from '../features/DishContainer/hooks/useExcludeDishes';
import useGlobalNewPpsis from '../features/PpsiContainer/hooks/useGlobalNewPpsis';
import { getPpsiStatus } from '../features/PpsiContainer/util';
import { EnrichmentData, States } from '../features/PpsiContainer/constants';
import { ppsiCreateFromExistingRoute, ppsiCreateRoute } from '../routes';
import { toJS } from '../utils/immutableUtils';
import View from './views';
import PaperContainer from '../components/common/PaperContainer';

function getGlobalPpsiStatus(rowData) {
  return getPpsiStatus(rowData.globalPpsi) || 'NOT STARTED';
}

const Columns = {
  GlobalPpsiStatus: {
    name: 'globalPpsiStatus',
    label: 'Global PPSI Status',
    sortable: false,
    filterable: true,
    calculateCellValue: rowData => getGlobalPpsiStatus(rowData),
    CellValue: ({ cellValue }) => {
      return (
        <Chip
          label={<CellValueHighlighter cellValue={cellValue} />}
          size="medium"
        />
      );
    },
    width: 150,
    grow: 0,
    shrink: 0
  },
  LifeCycleStatus: {
    name: 'lifecycleStatus',
    // dataField: 'lifecycleStatus',
    calculateCellValue: rowData => {
      return (
        rowData.publishedPpsiEnrichmentData.referencedIFoodData
          .lifecycleStatus || ''
      );
    },
    // dataType: DataTypes.Date,
    label: 'Lifecycle Status',
    // searchable: false,
    filterable: true,
    sortable: true,
    width: 400
    // grow: 0
  },
  GlobalPpsiActualPublishDate: {
    name: 'globalPpsiActualPublishDate',
    dataField: `globalPpsi.${EnrichmentData.PUBLISHED}.actualPublishedDate`,
    dataType: DataTypes.Date,
    label: 'Publishing Date',
    searchable: false,
    width: 150,
    grow: 0,
    shrink: 0
  },
  GlobalPpsiPublishedBy: {
    name: 'publishedBy',
    dataField: `globalPpsi.${EnrichmentData.PUBLISHED}.publishBy`,
    label: 'Published By',
    width: 270
  },
  CreateFromGlobalButton: {
    name: 'createFromGlobalButton',
    label: '',
    sortable: false,
    calculateCellValue: rowData => getGlobalPpsiStatus(rowData),
    CellValue: ({ cellValue, rowData }) => {
      const { globalPpsi, formulaCode } = rowData.data;
      const createRoute = ppsiCreateRoute(formulaCode);
      if (cellValue === States.PUBLISHED) {
        const copyRoute = ppsiCreateFromExistingRoute(
          formulaCode,
          globalPpsi.id,
          globalPpsi.publishedPpsiEnrichmentData.id
        );
        return (
          // changes for CR - IFPPSI - 171 - remove "create instructions" option
          <Button route={copyRoute}>
            <FormattedMessage id="ppsi.copyInstruction" />
          </Button>
          // <ButtonGroup
          //   route={copyRoute}
          //   menuItems={[
          //     {
          //       text: <FormattedMessage id="ppsi.createInstruction" />,
          //       route: createRoute
          //     }
          //   ]}
          // >
          //   <FormattedMessage id="ppsi.copyInstruction" />
          // </ButtonGroup>
        );
      }
      return null;
      // (<Button route={createRoute}>
      //   <FormattedMessage id="ppsi.createInstruction" />
      // </Button>);
    },
    width: 160,
    grow: 0,
    shrink: 0
  }
};

/**
 * Creates map with mappings between the Global PPSIs and the local IDFs. The
 * key in the map is the formula code of the local IDF (i.e. IDF with a market
 * that matches the one of the current user) and the value is the Global PPSI.
 *
 * Note: Only one Global PPSI is supported per formula code.
 *
 * @param globalPpsis
 * @returns {Map<any, any>}
 */
function getPpsiMapByFormula(globalPpsis) {
  const ppsisByFormula = new Map();
  globalPpsis.forEach(globalPpsi => {
    const formulaCodes = globalPpsi.iFoodDishes;
    formulaCodes.forEach(formulaCode => {
      if (ppsisByFormula.has(formulaCode)) {
        console.warn('Global PPSI already mapped to formula', formulaCode);
      }
      ppsisByFormula.set(formulaCode, globalPpsi);
    });
  });
  return ppsisByFormula;
}

function decorateWithPpsiData(dishes, globalPpsis) {
  const ppsisByFormulaCode = getPpsiMapByFormula(globalPpsis);

  const dishesWithPpsi = dishes.map(dish => {
    const globalPpsi = ppsisByFormulaCode.get(dish.formulaCode);
    return { ...dish, globalPpsi };
  });

  return dishesWithPpsi;
}

/**
 * Table displaying local dishes which are based on global ones. Global PPSIs
 * are mapped to each local dish, if it exists and is in state nnn the
 * "Create Instruction" button is displayed.
 *
 * Each row in the table represents a local IDF (based on global IDF). Global
 * PPSIs are NOT used as rows due to:
 *
 * (1) Local IDFs where no Global PPSI yet exists are NOT displayed, and
 *
 * (2) Support cases when one Global PPSI maps to multiple IDFs
 *
 * Only one PPSI is displayed per IDF, i.e. multiple PPSIs applicable for one
 * local IDF is not supported. If it occurs a warning is written on the
 * browser's console and an arbitrary PPSI is selected.
 *
 * @returns {*}
 * @constructor
 */
const NewDishesGlobalView = () => {
  const filter = useMemo(
    () => ({
      isGlobalBased: true,
      hasPpsi: false
    }),
    []
  );
  const status = 'GLOBAL';
  const [isFetching, dishesRaw] = useDishes(filter, status);
  const [isFetchingPpsis, globalPpsisRaw] = useGlobalNewPpsis();
  const dishes = toJS(dishesRaw, []);
  const globalPpsis = toJS(globalPpsisRaw, []);
  const dishesWithPpsi = decorateWithPpsiData(dishes, globalPpsis);
  const [excludeDishes] = useExcludeDishes();

  View.NewGlobalDishes.visit();

  function clickExcludeDishes(selectedData, callback) {
    const formulaCodes = selectedData.map(row => row.data.formulaCode);
    excludeDishes(formulaCodes);
    callback();
  }

  return (
    <PaperContainer showProgress={isFetching || isFetchingPpsis}>
      <Table
        title="ppsi.newGlobalPpsisTableTitle"
        data={dishesWithPpsi}
        columns={[
          DishColumns.IfoodID,
          DishColumns.PresentationName,
          DishColumns.PiaNumber,
          DishColumns.LifeCycleStatus,
          DishColumns.ModifyDate,
          Columns.GlobalPpsiStatus,
          Columns.GlobalPpsiActualPublishDate,
          // Columns.GlobalPpsiPublishedBy,
          Columns.CreateFromGlobalButton
        ]}
        getObjectId={object => object.id}
        defaultSorting={[{ columnName: 'presentationName', direction: 'asc' }]}
        selectable
        showLanguageSelector={false}
        actions={[{ label: 'Exclude', onClick: clickExcludeDishes }]}
      />
    </PaperContainer>
  );
};

export default NewDishesGlobalView;
